import { cloneDeep } from "lodash";

import { AutoCompleteTarget, IFormSchemaV2 } from "@graffer-inc/form-schema";

import {
  isReplacedApplicantCorporateSearchComponent,
  ReplacedComponent,
} from "../../domain/procedure/form-schema/applicant-schema";
import { SmartApplyFormSchemaValidator } from "../../domain/procedure/form-schema/smart-apply-form-schema-validator";
import { MigrationError } from "../error";
import { CloudGovFormSchemaAccessor } from "../../domain/procedure/form-schema/smart-apply-form-schema-validator/accessor-helper";
import { CloudGovFormSchema } from "../../domain/procedure/form-schema/cloudgov-form-schema";
import { hasIgnorableValidationErrorsOnly } from "../util";
import { DefaultApplicantSchemaKeys } from "../../domain/procedure/procedure-detail";

type Response = {
  migrated?: IFormSchemaV2;
  migrationError?: MigrationError;
};

export const up = (formSchema: IFormSchemaV2): Response => {
  if (!isValidSchemaToStartMigration(formSchema)) {
    return {
      migrationError: new MigrationError(
        "VALIDATION_ERROR_BEFORE_MIGRATION",
        new SmartApplyFormSchemaValidator(
          formSchema,
          DefaultApplicantSchemaKeys,
        ).validateDeep(),
      ),
    };
  }

  if (
    formSchema.root[0].children.some((element) =>
      isReplacedApplicantCorporateSearchComponent(
        element,
        DefaultApplicantSchemaKeys,
      ),
    )
  ) {
    return {
      migrationError: new MigrationError("MIGRATION_UNNECESSARY", [
        "法人検索コンポーネントがすでに存在するため、マイグレーションの必要がありません",
      ]),
    };
  }

  const applicantCorporateSearch = cloneDeep(
    ReplacedComponent.applicantCorporateSearch,
  );
  Object.entries(applicantCorporateSearch.autocompleteTarget).forEach(
    ([key, value]) => {
      if (formSchema.root[0].children.some(({ id }) => id === value)) {
        return;
      }
      delete applicantCorporateSearch.autocompleteTarget[
        key as keyof AutoCompleteTarget
      ];
    },
  );
  formSchema.root[0].children.splice(1, 0, applicantCorporateSearch);

  const formSchemaInstance = CloudGovFormSchema.fromJSON(formSchema);
  const expectedMigrationTargetRulesCount = new CloudGovFormSchemaAccessor(
    formSchema,
    DefaultApplicantSchemaKeys,
  ).supportedApplicantTypes.length;
  let actualMigrationTargetRulesCount = 0;
  formSchema.conditionalRuleSet!.conditionalRules.forEach(
    (conditionalRule, index) => {
      if (
        !formSchemaInstance.isApplicantSectionConditionalRules(
          index,
          DefaultApplicantSchemaKeys,
        )
      ) {
        return;
      }

      const { match } = conditionalRule.if.conditions[0];
      conditionalRule.then.rules.splice(0, 0, {
        id: DefaultApplicantSchemaKeys.applicantCorporateSearch.id,
        rule: {
          hidden: match.const !== "CORPORATE",
        },
      });
      actualMigrationTargetRulesCount++;
    },
  );
  if (actualMigrationTargetRulesCount !== expectedMigrationTargetRulesCount) {
    return {
      migrationError: new MigrationError("VALIDATION_ERROR_BEFORE_MIGRATION", [
        "申請者セクションに関わる条件付きルールが想定しない数で設定されており、正しくマイグレーションを実行できません。",
      ]),
    };
  }

  // class の constructor が呼ばれるとプロパティが補完されることがあり、タイミングによっては意図しないバグが発生するので、
  // 一度 constructor を挟む。
  // https://graffer.slack.com/archives/C01D05MFB6D/p1672799479869419?thread_ts=1672793501.454379&cid=C01D05MFB6D
  //
  // また、次の観点から、 CloudGovFormSchema の toJSON ではなく、 JSON.parse(JSON.stringify()) を用いる
  // - テストコードにおいて json file と比較する場合に undefined などの扱いが厄介
  // - backend/node が web api として関数の結果をデシリアライズする場合は結局 JSON.stringify が呼ばれる
  //  - https://github.com/expressjs/express/blob/8368dc178af16b91b576c4c1d135f701a0007e5d/lib/response.js#L271
  const migrated: IFormSchemaV2 = JSON.parse(
    JSON.stringify(new CloudGovFormSchema(formSchema)),
  );

  if (!isValidSchemaToFinishMigration(migrated)) {
    return {
      migrated,
      migrationError: new MigrationError(
        "VALIDATION_ERROR_AFTER_MIGRATION",
        new SmartApplyFormSchemaValidator(
          migrated,
          DefaultApplicantSchemaKeys,
        ).validateDeep(),
      ),
    };
  }

  return {
    migrated,
  };
};

const isValidSchemaToStartMigration = (formSchema: IFormSchemaV2): boolean => {
  const validator = new SmartApplyFormSchemaValidator(
    formSchema,
    DefaultApplicantSchemaKeys,
  );
  if (validator.isValid()) {
    return true;
  }

  return hasIgnorableValidationErrorsOnly(validator);
};

const isValidSchemaToFinishMigration = (formSchema: IFormSchemaV2): boolean => {
  const validator = new SmartApplyFormSchemaValidator(
    formSchema,
    DefaultApplicantSchemaKeys,
  );
  if (validator.isValid()) {
    return true;
  }

  return hasIgnorableValidationErrorsOnly(validator);
};
